import React from "react";
import { useTheme } from "next-themes";
import { useNavigate } from "react-router-dom";
import {
  PenTool,
  Monitor,
  Printer,
  Share2,
  Layers,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import SectionHeader from "@/components/brams/SectionHeader";
import Reveal from "@/components/brams/Reveal";

const ACCENT = "#E11D2E";

const services = [
  {
    icon: PenTool,
    title: "Création de Logo & Identité Visuelle",
    desc: "Création de logos uniques, identités visuelles complètes et chartes graphiques professionnelles pour construire une image de marque forte, cohérente et mémorable.",
    href: "/services/logos-branding",
  },
  {
    icon: Monitor,
    title: "Web Design & Sites Internet",
    desc: "Conception de sites vitrines et boutiques en ligne modernes, rapides, responsives et optimisés pour offrir une excellente expérience utilisateur.",
    href: "/services/web-design",
  },
  {
    icon: Layers,
    title: "Flyers & Supports Imprimés",
    desc: "Création de flyers, affiches, cartes de visite, menus, brochures, bâches, roll-ups et tous vos supports de communication imprimés.",
    href: "/services/flyers-print",
  },
  {
    icon: Share2,
    title: "Design Réseaux Sociaux",
    desc: "Création de publications, stories, bannières, carrousels et templates personnalisés pour Instagram, TikTok, Facebook, LinkedIn et autres plateformes.",
    href: "/services/social-media",
  },
  {
    icon: Printer,
    title: "Impression Professionnelle",
    desc: "Prise en charge de l'impression de tous vos supports de communication avec une finition professionnelle et des matériaux de qualité.",
    href: "/services/impression",
  },
  {
    icon: Sparkles,
    title: "Branding & Direction Artistique",
    desc: "Accompagnement stratégique pour développer une identité visuelle forte et cohérente, de la conception à la mise en œuvre de tous vos projets.",
    href: "/services/branding-direction",
  },
];

export default function Services() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="Nos Services"
          title={
            <>
              Design avec <em className="not-italic opacity-60">intention.</em>
              <br /> Identité qui dure.
            </>
          }
          subtitle="Du premier concept à la charte finale — chaque détail réfléchi."
        />

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 60}>
              <ServiceCard service={s} isDark={isDark} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, isDark, index }) {
  const Icon = service.icon;
  const navigate = useNavigate();
  const onClick = () => {
    if (service.href) navigate(service.href);
  };
  return (
    <div
      data-testid={`service-card-${index}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "group relative p-10 transition-all duration-500 ease-in-out hover:-translate-y-1 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-offset-0",
        isDark
          ? "bg-black hover:bg-white/[0.02] border border-white/[0.06]"
          : "bg-white hover:bg-black/[0.015] border border-black/[0.06]"
      )}
    >
      {/* Top accent line */}
      <div
        className="absolute left-0 top-0 h-px w-0 group-hover:w-full transition-all duration-700"
        style={{ background: ACCENT }}
      />
      <div className="flex items-start justify-between mb-10">
        <div
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110",
            isDark ? "bg-white/5 border border-white/10" : "bg-black/5 border border-black/10"
          )}
        >
          <Icon size={20} strokeWidth={1.4} style={{ color: ACCENT }} />
        </div>
        <span
          className={cn(
            "text-xs font-display tracking-widest opacity-40",
            isDark ? "text-white" : "text-black"
          )}
        >
          0{index + 1}
        </span>
      </div>
      <h3 className="font-display font-semibold text-xl md:text-2xl tracking-tight mb-3 pr-8">
        {service.title}
      </h3>
      <p
        className={cn(
          "text-sm leading-relaxed font-body font-light",
          isDark ? "text-white/65" : "text-black/60"
        )}
      >
        {service.desc}
      </p>

      {/* Bottom hover indicator */}
      <div className="mt-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: ACCENT }}
          />
          <span
            className={cn(
              "text-[10px] uppercase tracking-[0.35em] font-medium",
              isDark ? "text-white/55" : "text-black/55"
            )}
          >
            Découvrir
          </span>
        </div>
        <div
          className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500",
            "group-hover:rotate-45",
            isDark
              ? "bg-white/[0.04] border border-white/10 group-hover:bg-white group-hover:text-black"
              : "bg-black/[0.04] border border-black/10 group-hover:bg-black group-hover:text-white"
          )}
        >
          <ArrowUpRight size={14} />
        </div>
      </div>
    </div>
  );
}
